/**
 * InterviewQuestion Component - Display Current Question
 * Shows interviewer info and question with timer
 */
import React, { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, Users, Zap, MessageSquare, Clock } from "lucide-react";

const InterviewQuestion = memo(({ 
  question, 
  interviewer,
  questionNumber,
  totalQuestions,
  mode,
  difficulty,
  timeRemaining,
  formatTime
}) => {
  const getModeIcon = (mode) => {
    switch (mode) {
      case "panel": return <Users className="w-5 h-5" />;
      case "stress": return <Zap className="w-5 h-5" />;
      default: return <MessageSquare className="w-5 h-5" />;
    }
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case "easy": return "bg-green-100 text-green-700";
      case "medium": return "bg-yellow-100 text-yellow-700";
      case "hard": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <Card className="border-0 shadow-xl bg-white/90 backdrop-blur">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {/* Interviewer Avatar */}
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            
            <div>
              <CardTitle className="text-lg">
                {interviewer?.name || "AI Interviewer"}
              </CardTitle>
              <p className="text-sm text-gray-500">
                {interviewer?.role || "Technical Interviewer"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Question Counter */}
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              Q{questionNumber}/{totalQuestions}
            </Badge>
            
            {/* Mode Badge */}
            <Badge variant="outline" className="bg-purple-50 text-purple-700">
              {getModeIcon(mode)}
              <span className="ml-1 capitalize">{mode}</span>
            </Badge>
            
            {/* Difficulty Badge */}
            {difficulty && (
              <Badge className={getDifficultyColor(difficulty)}>
                {difficulty}
              </Badge>
            )}

            {/* Timer */}
            {timeRemaining > 0 && (
              <Badge variant="outline" className={`${
                timeRemaining <= 30 ? 'bg-red-50 text-red-700 animate-pulse' : 'bg-gray-50'
              }`}>
                <Clock className="w-3 h-3 mr-1" />
                {formatTime(timeRemaining)}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Question Text */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-xl border">
          <p className="text-lg text-gray-800 leading-relaxed font-medium">
            "{question}"
          </p>
        </div>

        {/* Interviewer Hint */}
        {interviewer?.style && (
          <p className="text-sm text-gray-500 mt-3 italic">
            Style: {interviewer.style}
          </p>
        )}
      </CardContent>
    </Card>
  );
});

InterviewQuestion.displayName = "InterviewQuestion";

export default InterviewQuestion;
